import { LabelSelector, type SeverityLabelOption, type LabelWithSeverity, type SeverityConfig, getSeverityLabel } from '@core/components/LabelSelector';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useMemo } from 'react';

import type { DialogTextLabels } from '../../types';
import { useDialogContext } from '../DialogProvider';

import { stepFormStyles, inputGroupStyles } from './Steps.css';

/**
 * TextLabelsStep component props
 */
export interface TextLabelsStepProps {
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
}

/**
 * TextLabelsStep component - Step 2 of the dialog
 * Handles text content labeling configuration
 */
export function TextLabelsStep({ standardLabels, labelsLoading }: TextLabelsStepProps) {
  const { state, actions } = useDialogContext();
  const { textLabels } = state.formData;

  // Filter standard labels for text content
  const textLabelOptions: SeverityLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Text)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity ?? undefined,
        })),
    [standardLabels]
  );

  // Configuration for severity-based labels
  const severityConfig: SeverityConfig = useMemo(
    () => ({
      mode: 'severity' as const,
      min: 1,
      defaultValue: 2,
      defaultMax: 3,
      showDescription: true,
      getValueLabel: getSeverityLabel,
    }),
    []
  );

  const handleTextLabelsChange = (labels: LabelWithSeverity[]) => {
    const updatedTextLabels: DialogTextLabels = {
      textLabels: labels,
    };

    actions.updateFormData({
      textLabels: updatedTextLabels,
    });
  };

  return (
    <div className={stepFormStyles}>
      <div className={inputGroupStyles}>
        {textLabelOptions.length > 0 ? (
          <LabelSelector
            enableBulkSelection
            availableLabels={textLabelOptions}
            config={severityConfig}
            emptyStateMessage="No text labels selected. Add labels to define text content criteria."
            loading={labelsLoading}
            selectedLabels={textLabels.textLabels}
            title="Text Content Labels"
            onChange={handleTextLabelsChange}
          />
        ) : (
          <div>{labelsLoading ? <p>Loading text labels...</p> : <p>No text labels available. Please configure labels in the system settings.</p>}</div>
        )}
      </div>
    </div>
  );
}
